import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { withRouter, routerShape } from 'react-router';
import kebabCase from 'lodash/kebabCase';
import get from 'lodash/get';
import Dropdown from '../../components/Dropdown';
import Icon from '../../components/Icon';
import Dimmer from '../../components/Dimmer';
import Form from '../../components/Form';
import Toast from '../../components/Toast';
import Modal from '../../components/Modal';
import Loader from '../../components/Loader';
import Button from '../../components/Button';
import Label from '../../components/Label';
import Message from '../../components/Message';
import FilterPreview from '../../components/CollegeEventsFilterPreview';
import messages from './messages';
import targetMarketIcon from './target-market-icon';
import './styles.css';

const renameButton = 'renameButton';
const duplicateButton = 'duplicateButton';
const deleteButton = 'deleteButton';

const ConfirmDeleteModal = ({ open, loading, onConfirm, onCancel, message, mountNode }) =>
  (
    <Dimmer.Dimmable
      role="alert"
      aria-label="Delete Confirmation"
      as={Modal}
      mountNode={mountNode}
      open={open}
      onClose={onCancel}
      data-cy="college-events-filter-delete-confirmation"
    >
      <Dimmer
        tabIndex={-1}
        role="progressbar"
        aria-label="Loading"
        aria-busy={loading}
        inverted
        active={loading}
      >
        <Loader active={loading} />
      </Dimmer>
      <h2 styleName="header-padding">
        <FormattedMessage {...messages.confirmDeleteHeader} />
      </h2>
      <Modal.Content>
        <p><FormattedMessage {...message} /></p>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onCancel} styleName="cancel-delete">
          <FormattedMessage {...messages.confirmDeleteCancel} />
        </Button>
        <Button primary onClick={onConfirm}>
          <FormattedMessage {...messages.confirmDeleteYes} />
        </Button>
      </Modal.Actions>
    </Dimmer.Dimmable>
  );
ConfirmDeleteModal.propTypes = {
  open: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  message: PropTypes.object.isRequired,
  mountNode: PropTypes.object,
};

class FilterRow extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    filter: PropTypes.object.isRequired,
    router: routerShape.isRequired,
    renameCollegeEventFilter: PropTypes.func.isRequired,
    deleteCollegeEventFilter: PropTypes.func.isRequired,
  };

  options = [{
    'key': renameButton,
    'text': 'Rename',
    'value': renameButton,
    'accessibility': 'Rename',
    'data-cy': 'college-events-filter-rename',
  }, {
    'key': duplicateButton,
    'text': 'Duplicate',
    'value': duplicateButton,
    'accessibility': 'Duplicate',
    'data-cy': 'college-events-filter-duplicate',
  }, {
    'key': deleteButton,
    'text': 'Delete',
    'value': deleteButton,
    'accessibility': 'Delete',
    'data-cy': 'college-events-filter-delete',
  }];

  constructor(props) {
    super(props);
    this.state = {
      deleteConfirmOpen: false,
      renameInProgress: false,
      error: false,
      successTitle: false,
      validationError: false,
      missingFilterName: false,
      deleteConfirmMessage: messages.confirmDeleteMsg,
    };
  }

  onChangeMenuOption = (e, { value }) => {
    const { router } = this.props;
    const { push } = router;
    switch (value) {
      case renameButton:
        this.onRename();
        break;

      case duplicateButton: {
        const { filter } = this.props;
        push(`/college-events/view-filters/${filter.id}/duplicate`);
        break;
      }
      case deleteButton:
        this.onDelete();
        break;
    }
  }

  onRename = () => {
    this.setState({ renameInProgress: true, name: this.props.filter.name, successTitle: false });
  };

  onRenameChange = (e) => {
    // eslint-disable-next-line react/no-access-state-in-setstate
    const name = e.target.validity.valid ? e.target.value : this.state.name;
    this.setState({ name, validationError: false });
  };

  onReplaceSuccess = () => {
    const { intl } = this.props;
    this.setState({
      error: false,
      renameInProgress: false,
      successTitle: intl.formatMessage(messages.renameSuccessTitle),
      successDescription: intl.formatMessage(messages.renameSuccessDescription),
    });
  };

  onRenameSubmit = () => {
    const { filter, renameCollegeEventFilter } = this.props;
    let { name } = this.state;
    name = name.trim();
    if (name.length === 0) {
      const missingFilterName = true;
      this.setState({ validationError: true, missingFilterName });
    } else {
      this.setState({ error: false, missingFilterName: false });
      const variables = {
        input: {
          collegeEventFilter: { id: filter.id, name, version: filter.version },
        },
      };
      renameCollegeEventFilter({ variables }).then(this.onReplaceSuccess, this.showError);
    }
  };

  onDelete = () => {
    const deleteConfirmMessage = messages.confirmDeleteMsg;
    this.setState({ deleteConfirmOpen: true, successTitle: false, deleteConfirmMessage });
  };

  onDeleteSuccess = () => {
    const { intl } = this.props;
    this.setState({
      error: false,
      successTitle: intl.formatMessage(messages.deleteSuccessTitle),
      successDescription: intl.formatMessage(messages.deleteSuccessDescription),
    });
  };

  onDeleteConfirm = () => {
    const { filter, deleteCollegeEventFilter } = this.props;
    this.setState({ deleteConfirmOpen: false, error: false });
    const variables = {
      input: {
        collegeEventFilter: {
          id: filter.id,
        },
      },
    };
    deleteCollegeEventFilter({ variables }).then(this.onDeleteSuccess, this.showError);
  };

  onDeleteCancel = () => {
    this.setState({ deleteConfirmOpen: false, error: false });
  };

  setDeleteModalMountRef = (el) => {
    this.deleteModalTargetNode = el;
  }

  showError = (err) => {
    const errorMessage = get(err, 'graphQLErrors[0].message') || get(err, 'message') || '';
    const message = errorMessage.includes('name should be unique') ? messages.errorUniqueName : messages.error;
    const error = (
      <Message error>
        <FormattedMessage {...message} />
      </Message>
    );
    this.setState({ error });
  };

  render() {
    const { filter, intl } = this.props;
    const {
      error,
      deleteConfirmOpen,
      renameInProgress,
      name,
      successTitle,
      successDescription,
      validationError,
      missingFilterName,
      deleteConfirmMessage,
    } = this.state;
    const placeholder = intl.formatMessage(messages.renameName);

    return (
      <div styleName="row" ref={this.setDeleteModalMountRef} data-cy={`college-events-filter-row-${kebabCase(filter.name)}`}>
        <Dimmer.Dimmable styleName="cells">
          <div styleName="icon-cell" data-cy="college-events-filter-target">{targetMarketIcon}</div>
          <div styleName="main-cell">
            {!renameInProgress && <h3 tabIndex="0" data-cy="college-events-filter-header">{filter.name}</h3>}
            {renameInProgress && (
              <Form onSubmit={this.onRenameSubmit}>
                <Form.Group>
                  <Form.Input styleName="name-input" type="text" fluid error={validationError && !name.trim()}>
                    <input
                      aria-label={placeholder}
                      onChange={this.onRenameChange}
                      id="name"
                      name="name"
                      value={name}
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus
                      pattern="[a-zA-Z0-9 ]*"
                      placeholder={placeholder}
                      maxLength="256"
                      data-cy="college-event-filter-name-input"
                    />
                  </Form.Input>
                  <Form.Button primary data-cy="college-event-filter-name-submit">
                    <FormattedMessage {...messages.renameSubmit} />
                  </Form.Button>
                </Form.Group>
                {missingFilterName && (
                <Label role="alert" basic color="red" pointing>
                  <FormattedMessage {...messages.missingFilterName} />
                </Label>
                )}
              </Form>
            )}
            <div tabIndex="0"><FilterPreview {...filter} /></div>
          </div>
          <div styleName="stats-cell" tabIndex="0" data-cy="college-events-filter-assigned-to">
            <span styleName="stat-label"><FormattedMessage {...messages.assignedTo} /></span>
            <span styleName="stat-value">{filter.publishedCollegeEventCount}</span>
            <span styleName="stat-unit">
              <FormattedMessage {...messages.liveEvents} values={{ events: filter.publishedCollegeEventCount }} />
            </span>
          </div>
          <div styleName="stats-cell" tabIndex="0" data-cy="college-events-filter-recommended-to">
            <span styleName="stat-label"><FormattedMessage {...messages.recommendsTo} /></span>
            <span styleName="stat-value">{filter.filterMatches}</span>
            <span styleName="stat-unit"><FormattedMessage {...messages.students} values={{ students: filter.filterMatches }} /></span>
          </div>
          <div className="ui right pointing dropdown button" styleName="menu-cell">
            <Dropdown
              data-cy="college-events-filter-options-dropdown"
              icon={null}
              selectOnNavigation={false}
              selectOnBlur={false}
              trigger={(
                <Icon
                  link
                  name="ellipsis vertical"
                  size="large"
                  aria-label="Filter Options"
                  styleName="menu-icon"
                />
              )}
              onChange={this.onChangeMenuOption}
              options={this.options}
              value={null}
            />
          </div>
        </Dimmer.Dimmable>
        <ConfirmDeleteModal
          open={deleteConfirmOpen}
          onConfirm={this.onDeleteConfirm}
          onCancel={this.onDeleteCancel}
          loading={false}
          message={deleteConfirmMessage}
          mountNode={this.deleteModalTargetNode}
        />
        {error}
        <Toast
          active={!!successTitle}
          title={successTitle}
          description={successDescription}
          onDeactivate={() => { this.setState({ successTitle: false }); }}
        />
      </div>
    );
  }
}

export default withRouter(injectIntl(FilterRow), { withRef: true });
